<template>
  <div>
    <!-- Backdrop overlay for mobile -->
    <transition name="fade">
      <div
        v-if="isSidebarOpen && isMobileView"
        class="sidebar-backdrop"
        @click="closeSidebar"
      ></div>
    </transition>

    <!-- Sidebar -->
    <aside class="sidebar" :class="{ open: isSidebarOpen || !isMobileView }">
      <!-- Logo -->
      <div class="logo">
        <div class="logo-icon">$</div>
        <div class="logo-text">
          <h1>FinanceHub</h1>
          <p>Enterprise</p>
        </div>
      </div>

      <!-- Menu -->
      <nav class="menu">
        <router-link
          v-for="item in menu"
          :key="item.name"
          :to="item.path"
          class="menu-item"
          active-class="active"
          @click.native="handleMenuClick"
        >
          <span class="icon">{{ item.icon }}</span>
          <strong class="label">{{ item.name }}</strong>
        </router-link>
      </nav>

      <!-- Footer -->
      <div class="sidebar-footer">
        <div class="user">
          <div class="avatar">{{ userInitial }}</div>
          <div class="user-info">
            <strong>{{ userName }}</strong>
            <span>{{ userEmail }}</span>
          </div>
        </div>

        <button class="logout" @click="handleLogout" :disabled="loggingOut">
          {{ loggingOut ? 'Signing out...' : 'Sign out' }}
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'Sidebar',
  data() {
    return {
      menu: [
        { name: 'Dashboard', path: '/dashboard', icon: '\u25A6' },
        { name: 'Receipts', path: '/receipts', icon: '\uD83D\uDCB2' },
        { name: 'Expenses', path: '/expenses', icon: '\uD83D\uDCB3' },
        { name: 'Salaries', path: '/salaries', icon: '\uD83D\uDCBC' },
        { name: 'Cash & Bank', path: '/cash-bank', icon: '\uD83C\uDFE6' },
        { name: 'Chart of Accounts', path: '/chart-of-accounts', icon: '\uD83D\uDCD6' },
        { name: 'Accounting Years', path: '/settings/accounting-years', icon: '\uD83D\uDCC5' },
        { name: 'Users', path: '/settings/users', icon: '\uD83D\uDC65' }
      ],
      loggingOut: false,
      windowWidth: window.innerWidth
    }
  },
  computed: {
    ...mapGetters('auth', ['getUser']),
    ...mapGetters('ui', ['isSidebarOpen']),
    userName() {
      return this.getUser?.fullName || 'User'
    },
    userEmail() {
      return this.getUser?.email || ''
    },
    userInitial() {
      return this.userName.charAt(0).toUpperCase()
    },
    isMobileView() {
      return this.windowWidth <= 1024
    }
  },
  watch: {
    $route() {
      // Close sidebar on route change for mobile
      if (this.isMobileView && this.isSidebarOpen) {
        this.closeSidebar()
      }
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('ui', ['closeSidebar']),
    async handleLogout() {
      this.loggingOut = true
      try {
        await this.logout()
      } finally {
        this.loggingOut = false
      }
    },
    handleMenuClick() {
      if (this.isMobileView) {
        this.closeSidebar()
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
/* Backdrop */
.sidebar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Sidebar */
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  width: 280px;
  height: 100vh;
  background: linear-gradient(180deg, #0f172a, #020617);
  color: #e5e7eb;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 18px;
  overflow-y: auto;
  z-index: 1000;
  transition: transform 0.3s ease;
}

/* Logo */
.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.logo-icon {
  width: 44px;
  height: 44px;
  background: #2563eb;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: bold;
  color: white;
}

.logo-text h1 {
  font-size: 18px;
  margin: 0;
}

.logo-text p {
  font-size: 13px;
  color: #94a3b8;
}

/* Menu */
.menu {
  flex: 1;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  margin-bottom: 6px;
  border-radius: 12px;
  color: #cbd5f5;
  text-decoration: none;
  transition: all 0.3s ease;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.menu-item.active {
  background: #2563eb;
  color: white;
}

.icon {
  font-size: 18px;
}

.label {
  font-size: 15px;
  font-weight: 500;
}

/* Footer */
.sidebar-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
}

.user {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.avatar {
  width: 42px;
  height: 42px;
  background: #1e40af;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.user-info strong {
  font-size: 14px;
}

.user-info span {
  display: block;
  font-size: 12px;
  color: #94a3b8;
}

.logout {
  background: transparent;
  border: none;
  color: #e5e7eb;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  padding: 7px 25px;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.logout:hover {
  background: rgba(255, 255, 255, 0.05);
}

.logout:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Responsive - Tablet and Mobile */
@media (max-width: 1024px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar.open {
    transform: translateX(0);
  }
}
</style>
